
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import {
  Brain,
  Sparkles,
  Settings,
  Zap,
  Workflow,
  Code,
  RotateCcw,
  Bot,
  Plus,
  Lock,
  ChevronRight,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

// Agent type definition
interface Agent {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  category: "assistant" | "automation" | "custom";
  status: "ready" | "running" | "stopped" | "locked";
  usageCount: number;
  isPremium: boolean;
}

// Sample agents data
const sampleAgents: Agent[] = [
  {
    id: "1",
    name: "Research Assistant",
    description: "Autonomously searches for information and prepares detailed reports on any topic.",
    icon: Brain,
    category: "assistant",
    status: "ready",
    usageCount: 12,
    isPremium: true
  },
  {
    id: "2",
    name: "Content Generator",
    description: "Creates blog posts, articles, and social media content based on your specifications.",
    icon: Sparkles,
    category: "assistant",
    status: "ready",
    usageCount: 5,
    isPremium: true
  },
  {
    id: "3",
    name: "Code Analyzer",
    description: "Reviews your code, detects issues, and suggests improvements.",
    icon: Code,
    category: "automation",
    status: "ready",
    usageCount: 8,
    isPremium: true
  },
  {
    id: "4",
    name: "Workflow Automator",
    description: "Automates repetitive tasks across your projects and workflows.",
    icon: Workflow,
    category: "automation",
    status: "running",
    usageCount: 3,
    isPremium: true
  },
  {
    id: "5",
    name: "Data Processor",
    description: "Extracts, transforms, and analyzes data from various sources.",
    icon: Zap,
    category: "automation",
    status: "stopped",
    usageCount: 7,
    isPremium: true
  },
  {
    id: "6",
    name: "Meeting Summarizer",
    description: "Records and creates concise summaries of your meetings.",
    icon: Bot,
    category: "assistant",
    status: "locked",
    usageCount: 0,
    isPremium: true
  }
];

const AIAgents = () => {
  const { isPremium, hasPremiumAccess } = useUserStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agents, setAgents] = useState<Agent[]>(sampleAgents);
  const [activeTab, setActiveTab] = useState("all");
  const [automationUsage, setAutomationUsage] = useState(45); // Percentage of automation credits used

  useEffect(() => {
    // Redirect if not premium
    if (!hasPremiumAccess('ai-agents')) {
      toast({
        title: "Premium Feature",
        description: "AI Agents & Automation is only available for premium users.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [isPremium, navigate, toast, hasPremiumAccess]);

  const filteredAgents = agents.filter(agent => {
    if (activeTab === "all") return true;
    if (activeTab === "assistants") return agent.category === "assistant";
    if (activeTab === "automation") return agent.category === "automation";
    if (activeTab === "custom") return agent.category === "custom";
    return true;
  });

  const handleAgentAction = (agentId: string, action: string) => {
    setAgents(prevAgents => 
      prevAgents.map(agent => {
        if (agent.id === agentId) {
          if (action === "start") {
            toast({
              title: "Agent Started",
              description: `${agent.name} is now running.`,
              variant: "success",
            });
            return { ...agent, status: "running" };
          } else if (action === "stop") {
            toast({
              title: "Agent Stopped",
              description: `${agent.name} has been stopped.`,
              variant: "default",
            });
            return { ...agent, status: "stopped" };
          }
        }
        return agent;
      })
    );
  };

  const handleUnlockAgent = (agentId: string) => {
    setAgents(prevAgents => 
      prevAgents.map(agent => {
        if (agent.id === agentId && agent.status === "locked") {
          toast({
            title: "Agent Unlocked",
            description: `${agent.name} is now available to use.`,
            variant: "success",
          });
          return { ...agent, status: "ready" };
        }
        return agent;
      })
    );
  };

  const createCustomAgent = () => {
    toast({
      title: "Coming Soon",
      description: "Custom agent creation will be available in the next update.",
      variant: "default",
    });
  };

  return (
    <div className="container mx-auto px-4 py-8 mt-16 mb-8">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-transparent bg-clip-text">
            AI Agents & Automation
          </h1>
          <p className="text-gray-600 mt-2">
            Deploy intelligent agents to automate your work and enhance productivity
          </p>
        </div>
        
        <Button onClick={createCustomAgent} className="mt-4 md:mt-0 bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700">
          <Plus className="mr-2 h-4 w-4" />
          Create Custom Agent
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-semibold flex items-center text-indigo-600">
              <Bot className="mr-2 h-5 w-5" />
              Active Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {agents.filter(a => a.status === "running").length}
            </div>
            <p className="text-sm text-gray-500">Currently running</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-semibold flex items-center text-indigo-600">
              <Zap className="mr-2 h-5 w-5" />
              Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Automation credits</span>
                <span className="text-sm font-medium">{automationUsage}%</span>
              </div>
              <Progress value={automationUsage} className="h-2" />
              <p className="text-xs text-gray-500 mt-2">
                {5000 - Math.floor(5000 * (automationUsage / 100))} credits remaining
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-semibold flex items-center text-indigo-600">
              <Sparkles className="mr-2 h-5 w-5" />
              Available Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {agents.filter(a => a.status !== "locked").length}
            </div>
            <p className="text-sm text-gray-500">Ready to deploy</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Agent Cards */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-gray-100/80 p-1">
          <TabsTrigger value="all" className="flex-1 text-sm py-2">
            All Agents
          </TabsTrigger>
          <TabsTrigger value="assistants" className="flex-1 text-sm py-2">
            AI Assistants
          </TabsTrigger>
          <TabsTrigger value="automation" className="flex-1 text-sm py-2">
            Automation
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex-1 text-sm py-2">
            Custom Agents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <AgentCard 
                key={agent.id} 
                agent={agent} 
                onAction={handleAgentAction}
                onUnlock={handleUnlockAgent}
              />
            ))}
          </div>
          
          {filteredAgents.length === 0 && (
            <div className="text-center py-12">
              <Bot className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium">No agents found</h3>
              <p className="mt-2 text-gray-500">
                You haven't created any custom agents yet.
              </p>
              <Button onClick={createCustomAgent} className="mt-4">
                Create Your First Agent
              </Button>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="assistants" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <AgentCard 
                key={agent.id} 
                agent={agent} 
                onAction={handleAgentAction}
                onUnlock={handleUnlockAgent}
              />
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="automation" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <AgentCard 
                key={agent.id} 
                agent={agent} 
                onAction={handleAgentAction}
                onUnlock={handleUnlockAgent}
              />
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="custom" className="mt-0">
          <div className="text-center py-12">
            <Plus className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium">Create Custom Agents</h3>
            <p className="mt-2 text-gray-500">
              Design your own AI agents to handle specific tasks for your projects.
            </p>
            <Button onClick={createCustomAgent} className="mt-4">
              Create Custom Agent
            </Button>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Advanced Features Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Advanced Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Workflow className="h-5 w-5 text-indigo-600" />
                <span>Workflow Builder</span>
              </CardTitle>
              <CardDescription>Connect multiple agents to create complex automations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mt-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-indigo-600" />
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Brain className="h-5 w-5 text-indigo-600" />
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                Start Building
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-600" />
                <span>Auto-Detect Tasks</span>
              </CardTitle>
              <CardDescription>Let AI analyze your projects and suggest automation opportunities</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mt-1 mb-4">
                Our AI will scan your project activities and suggest tasks that could be automated, saving you time and effort.
              </p>
            </CardContent>
            <CardFooter>
              <Button className="w-full bg-violet-600 hover:bg-violet-700">
                Scan Projects
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Agent Card Component
interface AgentCardProps {
  agent: Agent;
  onAction: (agentId: string, action: string) => void;
  onUnlock: (agentId: string) => void;
}

const AgentCard = ({ agent, onAction, onUnlock }: AgentCardProps) => {
  const Icon = agent.icon;
  
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-indigo-100 mr-3">
              <Icon className="h-5 w-5 text-indigo-600" />
            </div>
            <CardTitle className="text-lg">{agent.name}</CardTitle>
          </div>
          <StatusBadge status={agent.status} />
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <CardDescription className="min-h-[60px]">{agent.description}</CardDescription>
        
        <div className="mt-4 text-sm text-gray-500 flex items-center">
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          <span>Used {agent.usageCount} times</span>
        </div>
      </CardContent>
      <CardFooter className="pt-2 pb-4 flex justify-between">
        {agent.status === "locked" ? (
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={() => onUnlock(agent.id)}
          >
            <Lock className="h-4 w-4 mr-2" />
            Unlock Agent
          </Button>
        ) : agent.status === "running" ? (
          <Button 
            variant="outline" 
            className="w-full text-amber-600 border-amber-200 hover:bg-amber-50"
            onClick={() => onAction(agent.id, "stop")}
          >
            <span className="h-2 w-2 bg-amber-500 rounded-full animate-pulse mr-2"></span>
            Stop Agent
          </Button>
        ) : (
          <Button 
            variant="outline" 
            className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            onClick={() => onAction(agent.id, "start")}
          >
            <Zap className="h-4 w-4 mr-2" />
            Run Agent
          </Button>
        )}
        
        <Button 
          variant="ghost" 
          size="icon" 
          className="ml-2"
          onClick={() => {
            // Would navigate to agent detail page in a real app
          }}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

// Status Badge Component
const StatusBadge = ({ status }: { status: Agent["status"] }) => {
  switch (status) {
    case "ready":
      return (
        <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">
          Ready
        </Badge>
      );
    case "running":
      return (
        <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-200 flex items-center">
          <span className="h-1.5 w-1.5 bg-amber-500 rounded-full animate-pulse mr-1.5"></span>
          Running
        </Badge>
      );
    case "stopped":
      return (
        <Badge variant="outline" className="text-gray-600 bg-gray-50 border-gray-200">
          Stopped
        </Badge>
      );
    case "locked":
      return (
        <Badge variant="outline" className="text-indigo-600 bg-indigo-50 border-indigo-200 flex items-center">
          <Lock className="h-3 w-3 mr-1" />
          Premium
        </Badge>
      );
    default:
      return null;
  }
};

export default AIAgents;
